import HealthSourceInfo from "@/components/health/HealthSourceInfo";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dumbbell, Utensils, Target, TrendingUp, CalendarClock, CheckSquare, CalendarDays } from "lucide-react";
import ClientSessionsDialog from "@/components/trainer/ClientSessionsDialog";
import ClientWeeklyWorkoutModal from "@/components/trainer/ClientWeeklyWorkoutModal";
import ClientWeeklyMealModal from "@/components/trainer/ClientWeeklyMealModal";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Progress } from "@/components/ui/progress";
import { getUnitPrefs, KG_TO_LBS, CM_TO_IN } from "@/hooks/useUnitPrefs";
import { useTimeFormatter } from "@/lib/timeFormat";

const membershipColors = {
  free: "bg-muted text-muted-foreground",
  basic: "bg-primary/10 text-primary",
  premium: "bg-accent/10 text-accent",
  trainer: "bg-green-100 text-green-700",
};

const goalLabels = {
  weight: "Weight", body_fat: "Body Fat", chest: "Chest", waist: "Waist",
  hips: "Hips", arms: "Arms", thighs: "Thighs",
};

export default function ClientProfileDialog({ client, open, onOpenChange, workouts, templates = [] }) {
  const fmtTime = useTimeFormatter();
  const [showSessions, setShowSessions] = useState(false);
  const [showWeeklyWorkout, setShowWeeklyWorkout] = useState(false);
  const [showWeeklyMeal, setShowWeeklyMeal] = useState(false);
  const queryClient = useQueryClient();
  const unitPrefs = getUnitPrefs(client);

  const { data: progressLogs = [] } = useQuery({
    queryKey: ["clientProgress", client?.id],
    queryFn: () => base44.entities.ProgressLog.filter({ created_by_id: client.id }, "date", 20),
    enabled: !!client && open,
  });

  const { data: fitnessGoals = [] } = useQuery({
    queryKey: ["clientGoals", client?.id],
    queryFn: () => base44.entities.FitnessGoal.filter({ created_by_id: client.id }),
    enabled: !!client && open,
  });

  const { data: checkIns = [] } = useQuery({
    queryKey: ["clientCheckIns", client?.id],
    queryFn: () => base44.entities.CheckIn.filter({ user_id: client.id }, "-checked_in_date", 50),
    enabled: !!client && open,
  });

  const DAYS = ["sunday","monday","tuesday","wednesday","thursday","friday","saturday"];
  const todayKey = DAYS[new Date().getDay()];

  const { data: weeklyWorkout = null } = useQuery({
    queryKey: ["userWeeklyWorkoutPlan", client?.id],
    queryFn: () => base44.entities.UserWeeklyWorkoutPlan.filter({ user_id: client.id }).then(r => r[0] || null),
    enabled: !!client && open,
  });

  const { data: weeklyMeal = null } = useQuery({
    queryKey: ["userWeeklyMealPlan", client?.id],
    queryFn: () => base44.entities.UserWeeklyMealPlan.filter({ user_id: client.id }).then(r => r[0] || null),
    enabled: !!client && open,
  });

  const todayWorkoutId = weeklyWorkout?.[todayKey];
  const todayMealId = weeklyMeal?.[todayKey];

  const { data: todayWorkout = null } = useQuery({
    queryKey: ["workoutPlan", todayWorkoutId],
    queryFn: () => base44.entities.WorkoutPlan.filter({ id: todayWorkoutId }).then(r => r[0] || null),
    enabled: !!todayWorkoutId,
  });

  const { data: todayMeal = null } = useQuery({
    queryKey: ["mealPlanTemplate", todayMealId],
    queryFn: () => base44.entities.MealPlanTemplate.filter({ id: todayMealId }).then(r => r[0] || null),
    enabled: !!todayMealId,
  });



  if (!client) return null;

  const activeGoals = fitnessGoals.filter((g) => g.is_active);

  const convertValue = (value, goalType) => {
    if (!value) return value;
    if (goalType === "weight" && unitPrefs.useLbs) {
      return Math.round(value * KG_TO_LBS * 10) / 10;
    }
    if ((goalType === "chest" || goalType === "waist" || goalType === "hips" || goalType === "arms" || goalType === "thighs") && unitPrefs.useInches) {
      return Math.round(value * CM_TO_IN * 10) / 10;
    }
    return value;
  };

  const getDisplayUnit = (goalType, originalUnit) => {
    if (goalType === "weight" && unitPrefs.useLbs) return "lbs";
    if ((goalType === "chest" || goalType === "waist" || goalType === "hips" || goalType === "arms" || goalType === "thighs") && unitPrefs.useInches) return "in";
    return originalUnit;
  };

  const calculateProgress = (current, target) => {
    if (!current || !target || target <= 0) return 0;
    return Math.min(100, Math.max(0, (current / target) * 100));
  };

   const weightData = progressLogs
     .filter((l) => l.weight_kg)
     .sort((a, b) => new Date(a.date) - new Date(b.date))
     .map((l) => ({ date: l.date, weight: l.weight_kg }));

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto w-[calc(100%-2rem)] md:w-full rounded-xl md:rounded-lg">
        <DialogHeader>
          {/* Profile Header */}
          <div className="flex items-center gap-4 pb-2">
            {client.picture_url ? (
              <img src={client.picture_url} alt={client.full_name} className="w-16 h-16 rounded-full object-cover shrink-0" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-2xl shrink-0">
                {(client.full_name || client.email || "?")[0].toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-xl font-display">{client.full_name || "—"}</DialogTitle>
              <p className="text-sm text-muted-foreground">{client.email}</p>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="secondary" className={membershipColors[client.membership_type]}>
                  {client.membership_type}
                </Badge>
                {client.gender && <Badge variant="outline" className="text-xs">{client.gender}</Badge>}
                {client.age && <span className="text-xs text-muted-foreground">{client.age} yrs</span>}
              </div>
            </div>
            <Button size="sm" variant="outline" className="gap-1.5 shrink-0" onClick={() => setShowSessions(true)}>
              <CalendarClock className="w-3.5 h-3.5" /> Sessions
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-5">
          {/* Stats Row */}
          {(client.weight_kg || client.height_cm || client.body_fat_percentage) && (
            <div className="grid grid-cols-3 gap-3">
              {client.weight_kg && (
                <div className="rounded-xl bg-muted/50 p-3 text-center">
                  <p className="text-xs text-muted-foreground">Weight</p>
                  <p className="font-bold text-lg">{client.weight_kg}<span className="text-xs font-normal text-muted-foreground"> kg</span></p>
                </div>
              )}
              {client.height_cm && (
                <div className="rounded-xl bg-muted/50 p-3 text-center">
                  <p className="text-xs text-muted-foreground">Height</p>
                  <p className="font-bold text-lg">{client.height_cm}<span className="text-xs font-normal text-muted-foreground"> cm</span></p>
                </div>
              )}
              {client.body_fat_percentage && (
                <div className="rounded-xl bg-muted/50 p-3 text-center">
                  <p className="text-xs text-muted-foreground">Body Fat</p>
                  <p className="font-bold text-lg">{client.body_fat_percentage}<span className="text-xs font-normal text-muted-foreground"> %</span></p>
                </div>
              )}
            </div>
          )}

          {/* Weight Progress Chart */}
          {weightData.length > 1 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-4 h-4 text-primary" />
                <h3 className="font-semibold text-sm">Weight Progress</h3>
              </div>
              <div className="rounded-xl bg-muted/30 p-3">
                <ResponsiveContainer width="100%" height={160}>
                  <LineChart data={weightData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                    <YAxis tick={{ fontSize: 10 }} domain={["auto", "auto"]} />
                    <Tooltip formatter={(v) => [`${v} kg`, "Weight"]} labelFormatter={(d) => d} />
                    <Line type="monotone" dataKey="weight" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Fitness Goals */}
          {activeGoals.length > 0 && (
            <div className="rounded-xl bg-muted/40 p-4">
              <div className="flex items-center gap-2 mb-3">
                <Target className="w-4 h-4 text-primary" />
                <h3 className="font-semibold text-sm">Fitness Goals</h3>
              </div>
              <div className="grid gap-3">
                {activeGoals.map((goal) => {
                  const currentVal = convertValue(goal.current_value, goal.goal_type);
                  const targetVal = convertValue(goal.target_value, goal.goal_type);
                  const displayUnit = getDisplayUnit(goal.goal_type, goal.unit);
                  const progress = calculateProgress(currentVal, targetVal);
                  return (
                  <div key={goal.id} className="rounded-xl bg-muted/30 p-3 space-y-2">
                    <div className="flex items-start justify-between">
                      <p className="text-xs text-muted-foreground">{goalLabels[goal.goal_type] || goal.goal_type}</p>
                      {goal.target_date && (
                        <p className="text-[10px] text-muted-foreground">By {goal.target_date}</p>
                      )}
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-semibold">{currentVal || 0} / {targetVal} {displayUnit}</span>
                      <span className="text-xs text-muted-foreground">{Math.round(progress)}%</span>
                    </div>
                    <Progress value={progress} className="h-2" />
                  </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Today's Workout */}
          <div className="rounded-xl bg-muted/40 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Dumbbell className="w-4 h-4 text-primary" />
                <h3 className="font-semibold text-sm">Today's Workout</h3>
              </div>
              <Button size="sm" variant="outline" className="gap-1.5 h-7 text-xs" onClick={() => setShowWeeklyWorkout(true)}>
                <CalendarDays className="w-3 h-3" /> Weekly
              </Button>
            </div>
            {todayWorkout ? (
              <div className="space-y-2">
                <p className="font-semibold text-sm">{todayWorkout.name}</p>
                <div className="flex flex-wrap gap-1">
                  {todayWorkout.difficulty && <Badge variant="secondary" className="text-[10px]">{todayWorkout.difficulty?.replace(/\b\w/g, c => c.toUpperCase())}</Badge>}
                  {todayWorkout.goal && <Badge variant="outline" className="text-[10px]">{todayWorkout.goal?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</Badge>}
                  {todayWorkout.exercises?.length > 0 && <Badge variant="outline" className="text-[10px]">{todayWorkout.exercises.length} exercises</Badge>}
                </div>
                {todayWorkout.exercises?.length > 0 && (
                  <div className="space-y-1 pt-1">
                    {[...new Set(todayWorkout.exercises.map(e => e.day))].slice(0, 1).map(day => (
                      todayWorkout.exercises.filter(e => e.day === day).slice(0, 4).map((ex, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span className="w-1.5 h-1.5 rounded-full bg-primary/50 shrink-0" />
                          <span>{ex.name?.replace(/\b\w/g, c => c.toUpperCase())}</span>
                          {ex.sets && ex.reps && <span className="ml-auto text-[10px]">{ex.sets}×{ex.reps}</span>}
                        </div>
                      ))
                    ))}
                    {todayWorkout.exercises.length > 4 && (
                      <p className="text-[10px] text-muted-foreground pl-3">+{todayWorkout.exercises.length - 4} more</p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground italic">None assigned for today</p>
            )}
          </div>

          {/* Today's Meal */}
          <div className="rounded-xl bg-muted/40 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Utensils className="w-4 h-4 text-primary" />
                <h3 className="font-semibold text-sm">Today's Meal Plan</h3>
              </div>
              <Button size="sm" variant="outline" className="gap-1.5 h-7 text-xs" onClick={() => setShowWeeklyMeal(true)}>
                <CalendarDays className="w-3 h-3" /> Weekly
              </Button>
            </div>
            {todayMeal ? (
              <div className="space-y-2">
                <p className="font-semibold text-sm">{todayMeal.name}<HealthSourceInfo topic="planning" /></p>
                <div className="flex flex-wrap gap-1">
                  {todayMeal.goal && <Badge variant="secondary" className="text-[10px]">{todayMeal.goal?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</Badge>}
                  {todayMeal.daily_calories && <Badge variant="outline" className="text-[10px]">{todayMeal.daily_calories} kcal</Badge>}
                  {todayMeal.daily_protein_g && <Badge variant="outline" className="text-[10px]">{todayMeal.daily_protein_g}g protein</Badge>}
                </div>
                {todayMeal.meals?.length > 0 && (
                  <div className="space-y-1 pt-1">
                    {todayMeal.meals.slice(0, 4).map((meal, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary/50 shrink-0" />
                        <span>{meal.meal_name}</span>
                        {meal.time && <span className="ml-auto text-[10px]">{fmtTime(meal.time)}</span>}
                      </div>
                    ))}
                    {todayMeal.meals.length > 4 && (
                      <p className="text-[10px] text-muted-foreground pl-3">+{todayMeal.meals.length - 4} more</p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground italic">None assigned for today</p>
            )}
          </div>

          {/* Check-In History */}
          {checkIns.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <CheckSquare className="w-4 h-4 text-primary" />
                <h3 className="font-semibold text-sm">Check-In History</h3>
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {checkIns.map((checkIn) => (
                  <div key={checkIn.id} className="rounded-lg bg-muted/40 p-3 text-sm">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <p className="font-medium text-xs text-muted-foreground">
                        {new Date(checkIn.checked_in_date).toLocaleDateString()}
                      </p>
                      <span className="text-[10px] text-muted-foreground">{checkIn.week_key}</span>
                    </div>
                    {checkIn.note && <p className="text-foreground">{checkIn.note}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
          </div>
      </DialogContent>
    </Dialog>

    <ClientSessionsDialog
      client={client}
      open={showSessions}
      onOpenChange={setShowSessions}
    />
    <ClientWeeklyWorkoutModal
      client={client}
      open={showWeeklyWorkout}
      onOpenChange={setShowWeeklyWorkout}
    />
    <ClientWeeklyMealModal
      client={client}
      open={showWeeklyMeal}
      onOpenChange={setShowWeeklyMeal}
    />
    </>
  );
}